'use client';
import { useState, useEffect } from 'react';
import { BaseModal } from '../BaseModal/BaseModal';
import { ForgotPasswordModal } from '../ForgotPasswordModal/ForgotPasswordModal';
import { Button } from '../../Buttons/Button/Button';
import { AuthInput } from '../../Inputs/AuthInput/AuthInput';
import { passwordSchema } from '@practium/features/auth';
import styles from './ChangePasswordModal.module.css';

interface ChangePasswordModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (currentPassword: string, newPassword: string) => Promise<void>;
	isLoading?: boolean;
	userEmail?: string;
}

export const ChangePasswordModal = ({
	isOpen,
	onClose,
	onConfirm,
	isLoading,
	userEmail = '',
}: ChangePasswordModalProps) => {
	const [view, setView] = useState<'change' | 'forgot'>('change');
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [errors, setErrors] = useState<{ [key: string]: string }>({});

	useEffect(() => {
		if (!isOpen) setView('change');
	}, [isOpen]);

	const handleSubmit = async () => {
		setErrors({});

		const result = passwordSchema.safeParse({
			currentPassword,
			newPassword,
			confirmPassword,
		});

		if (!result.success) {
			const newErrors: { [key: string]: string } = {};
			result.error.issues.forEach((issue: any) => {
				const field = issue.path[0];
				newErrors[field] = issue.message;
			});
			setErrors(newErrors);
			return;
		}

		try {
			await onConfirm(currentPassword, newPassword);
			handleClose();
		} catch {
			setErrors({ general: 'Erro ao alterar senha. Verifique sua senha atual.' });
		}
	};

	const handleClose = () => {
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setErrors({});
		onClose();
	};

	const handleForgotPasswordClick = () => {
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setErrors({});
		setView('forgot');
	};

	return (
		<>
			<BaseModal
				isOpen={isOpen && view === 'change'}
				onClose={handleClose}
				title="Alterar Senha"
				isLoading={isLoading}
				actions={
					<>
						<Button
							variant="cancel"
							onClick={handleClose}
							disabled={isLoading}
							type="button"
							className={styles.actionButton}
						>
							Cancelar
						</Button>
						<Button
							variant="primary"
							onClick={handleSubmit}
							isLoading={isLoading}
							disabled={isLoading}
							type="button"
							className={styles.actionButton}
						>
							Alterar Senha
						</Button>
					</>
				}
			>
				<div className={styles.fields}>
					<AuthInput
						label="Senha Atual"
						isPassword
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						error={errors.currentPassword}
						placeholder="Digite sua senha atual"
						disabled={isLoading}
					/>

					<AuthInput
						label="Nova Senha"
						isPassword
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						error={errors.newPassword}
						placeholder="Mínimo 6 caracteres"
						disabled={isLoading}
					/>

					<AuthInput
						label="Confirmar Nova Senha"
						isPassword
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						error={errors.confirmPassword}
						placeholder="Digite novamente"
						disabled={isLoading}
					/>

					{errors.general && <p className={styles.errorGeneral}>{errors.general}</p>}

					<p className={styles.forgotHint}>
						Esqueceu sua senha?{' '}
						<button
							type="button"
							className={styles.forgotLink}
							onClick={handleForgotPasswordClick}
							disabled={isLoading}
						>
							Recuperar acesso
						</button>
					</p>
				</div>
			</BaseModal>

			<ForgotPasswordModal
				isOpen={isOpen && view === 'forgot'}
				onClose={() => setView('change')}
				onSuccess={handleClose}
				userEmail={userEmail}
			/>
		</>
	);
};
